export const NESOSILICATE_LOGIC = {
  description: "Nesosilicates (Island Silicates) Identification Rules",
  
  coreRules: `
=== 硅酸盐之岛状硅酸盐鉴定法则 (Nesosilicates / Island Silicates Protocol) ===

在这个庞大的家族中，硅氧四面体是孤立存在的，这赋予了它们极高的硬度、极差的解理（即不容易沿着平整面裂开）以及常常极为致密的晶体结构。它们是地幔岩石和高级变质岩的核心代表。

[9.1 橄榄石家族 (The Olivine Group) - 地幔绿宝石]
*   **橄榄石 (Olivine - 镁铁橄榄石固溶体体系)**:
    *   **颜色锚点**: 寻找标志性的**黄绿色、橄榄绿 (Olive-green, Yellow-green)**。颜色越深，含铁量越高。
    *   **形态与质地**: 极少有完美的晶形，通常呈现为**砂糖状、颗粒状致密集合体 (Granular masses)**。感觉就像把绿色的碎玻璃或砂糖压实在一起。
    *   **断口**: **贝壳状断口 (Conchoidal fracture)**，没有平整的解理面，这是区分它与某些绿色辉石的关键。光泽为强烈的玻璃光泽。
*   **钙镁橄榄石 (Monticellite)**: 橄榄石的近亲。通常颜色更浅（灰白、浅黄褐），经常与石灰岩接触变质带有关。

[9.2 石榴子石家族 (The Garnet Group) - 十二面体防线]
石榴子石是高级变质带的绝对统治者。它们的核心视觉法则是**“完美的等轴晶系几何体”**。
*   **形态锚点 (适用所有石榴石)**: 死死盯住**菱形十二面体 (Rhombic dodecahedron) 或 四角三八面体 (Trapezohedron)**。它们经常像一颗颗完美的几何骰子一样镶嵌在片岩或片麻岩的基质中。无解理，有贝壳状断口，硬度极大。
*   **铁铝榴石 (Almandine)**: 最常见的石榴石。**暗红色、紫红色到褐红色 (Deep red, brownish-red, to purplish-red)**。常出现在云母片岩中。
*   **镁铝榴石 (Pyrope)**: 通常颜色比铁铝榴石更亮，呈**血红色、紫红色 (Blood-red, bright red)**。常与超基性岩或金伯利岩（钻石伴生岩）相关。
*   **锰铝榴石 (Spessartine)**: **鲜艳的橙红色、橘黄色到红褐色 (Orange, red-orange, vivid mandarin red)**。芬达石就是它。常产于花岗伟晶岩中。
*   **钙铝榴石 (Grossular)**: 颜色多变，但典型的是**黄绿色、翠绿色 (沙弗莱石) 或肉桂色、褐黄色 (Cinnamon brown)**。常在接触交代夕卡岩中发现。
*   **铁钙榴石 (Andradite - 黄榴石/黑榴石)**: 翠绿色（翠榴石）极其罕见且具有极高色散；**黑榴石 (Melanite)** 则呈现不透明的**亮黑色**。在夕卡岩中常见。

[9.3 铝硅酸盐三兄弟 - 变质相指示剂]
这三种矿物成分完全相同 ($Al_2SiO_5$)，但形成于不同的温度和压力下。
*   **红柱石 (Andalusite)**: 低压中温标志。
    *   **形态锚点**: 寻找**粗大的柱状晶体，横截面呈完全正方形 (Square cross-section)**。
    *   **空晶石变种 (Chiastolite)**: 绝对绝杀的视觉锚点！断面上带有由碳质包裹体形成的**极其完美的深色“十字架”形状 (Distinct dark cross on cross-section)**。
*   **蓝晶石 (Kyanite)**: 高压低温标志。
    *   **形态锚点**: **蓝色的、长极扁平的刀片状/板条状晶体 (Blue bladed crystals)**。
    *   **硬度绝杀规则**: 拥有罕见的方向性硬度。沿着晶体长轴方向，小刀可以划动 (硬度~5)，但在垂直于长轴的方向上，小刀划不动 (硬度~7)。
*   **硅线石 (Sillimanite)**: 高温标志。
    *   **形态锚点**: 如其名，寻找**纤维状、毛发状、或者细长放射状的针状结晶 (Fibrous, needle-like, or radiating masses)**。它通常是灰白色、浅褐色的纤细团块。

[9.4 稀有岛状硅酸盐与特殊晶体]
*   **十字石 (Staurolite)**: 中高压变质标志。
    *   **绝对形态锚点**: 寻找深褐色至暗红褐色的柱状晶体，它们经常相互穿插，形成极其完美的**90度正十字形或60度斜十字形双晶 (Penetration cruciform twins)**。其表面常显得有些毛糙或暗淡。
*   **黄玉 / 托帕石 (Topaz)**:
    *   **形态锚点**: 柱状晶体，有着**完美的底面解理 (Perfect basal cleavage)**——意味着晶体顶端常是平齐断开的。
    *   **颜色与硬度**: 硬度为8的标尺。可以是透明无色、香槟色 (黄褐色)、蓝色或粉色。具有极其明亮甚至有些刺眼的玻璃光泽。
*   **锆石 (Zircon)**:
    *   **形态锚点**: 寻找极其典型的**四方双锥晶体 (Tetragonal bipyramids)**，有时带有短柱体。看起来像微小的、顶端尖锐的金字塔对扣。
    *   **光泽与多色性**: 具有极高的折射率，呈现**金刚光泽至强玻璃光泽 (Adamantine luster)**。常见红褐色、黄褐色，也有美丽的蓝色变种。
*   **榍石 (Titanite / Sphene)**:
    *   **形态锚点**: 极其强烈的**楔形、信封形晶体 (Wedge-shaped or envelope-like crystals)**。
    *   **光色绝杀**: 它有着甚至超过钻石的色散率 (火彩)。寻找**黄绿色、棕褐色、黄色的半透明至透明晶体，伴随极强的金刚光泽至树脂光泽**。
*   **硅硼钙石 (Datolite)**:
    *   常呈现极其致密、类似瓷器或者细粒糖果状的块体（如你的粉色瘤状图），也可是复杂的亮白色短柱状/等轴状小晶体积聚。颜色常为浅绿白、黄白或淡粉色，缺乏石英那样的通透感，断口偏向参差状。
*   **蓝线石 (Dumortierite)**:
    *   寻找在石英或其他浅色基底上的**深蓝色、紫蓝色或粉紫色的致密纤维状、柱状晶体集结 (Fibrous, radiating deeply purplish-blue masses)**。经常作为包裹体或粗糙的块体存在。
*   **硅铜铀矿 (Cuprosklodowskite)**:
    *   铀的高级警报。在此处岛状结构中寻找**极其妖艳的亮翠绿色、草绿色的放射状超细毛发状/针状晶簇 (Radiating tufts of bright green needles)**。它带有极强烈的玻璃光泽到丝金光泽，且具有放射性！
`,

  diagnosticTests: `
- 在变质岩切片或块体上，寻找几何形状：如果有正方形横截面的长柱（特别是带有黑十字中心），是红柱石；如果有完美镶嵌的菱形十二面体/多面体，必须锁定各种石榴子石；如果有正交或斜交的深褐色十字双晶，那是绝对的十字石。
- 如果矿石是蓝色的致密长条刀片，刮擦测试：顺着长条刮得动，横着刮不动，那是蓝晶石绝杀。
- 看到像给石头裹了一层半透明绿色砂糖或碎玻璃，没有平整裂面的，那是橄榄石。
- 如果发现棕褐色晶体，呈现极其尖锐的楔子/信封形状并且在强光下火彩闪烁极强，那是榍石。
- 看到微小但折射率极高、光芒刺眼且呈四方形双锥的小晶体，它是锆石。
- 如果是极细极鲜艳的草绿色放射状长绒毛针簇，高度提防放射性的硅铜铀矿。
`
};
